import { Box, ButtonDropdown, ButtonDropdownProps, Container, ExpandableSection, FormField, Header, Link, Popover, Select, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import React, { useContext, useEffect, useState } from 'react';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import { JournalLine } from 'src/models/calculation-builder/Journal';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import CONSTANTS from 'src/utils/constants';
import { CalculationBuilderContext } from '../CalculationBuilderView';
import JournalEntriesGrid from './JournalEntriesGrid';
import { reviewJournalsReducer } from '../../../services/calculation-builder/ReviewJournalsReducer';
import { initialState } from '../../../services/calculation-builder/ReviewJournalsState';
import { ACTIONS as CALC_BUILDER_ACTIONS } from '../../../services/calculation-builder/CalculationBuilderReducer';
import { ACTION_TYPE, TPEBasicModal } from 'src/components/shared/TPEBasicModal';
import { TPEUser } from 'src/models/users/TPEUser';
import { UserProfile } from 'src/models/users/UserProfile';
import { GlobalAppContext } from '../../App';
import ArrayUtils from 'src/utils/arrayUtils';
import TPEErrorWatcher from 'src/components/shared/TPEErrorWatcher';
import ATPUserCommentModal from '../../shared/ATPUserCommentModal';
import StringUtils from 'src/utils/stringUtils';
import {Checkbox} from "@amzn/awsui-components-react/polaris";
import moment from 'moment';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';

export default function ReviewJournalEntriesContainer(props: { expanded?: boolean, onCancel: () => void }) {
    const { expanded = false, onCancel } = props;
    const { globalDispatch, userProfile } = React.useContext(GlobalAppContext);
    const { services, calcBuilderState, calcBuilderDispatch } = useContext(CalculationBuilderContext);
    const [ state, dispatch ] = useReducerWithLogger(reviewJournalsReducer, initialState);
    const { canReviewAny } = getPermissions(AppModules.CALCULATION_BUILDER);

    const [ submitCalculationPayload, setSubmitCalculationPayload ] = useState(undefined as {calculationNumber: string, notes: string} | undefined);
    const [ calculationNumberForApproval, setCalculationNumberForApproval] = useState(undefined as string | undefined);
    const [ showSubmitConfirmationModal, setShowSubmitConfirmationModal ] = useState(false);
    const [ showApproveConfirmation, setShowApproveConfirmation ] = useState(false);
    const [ showRejectConfirmation, setShowRejectConfirmation ] = useState(false);
    const [ showUpdateExecutionGroupConfirmation, setShowUpdateExecutionGroupConfirmation ] = useState(false);
    const [ validationModalMessage, setValidationModalMessage ] = useState(null as string | null);
    const [loadTime, setLoadTime] = useState(null as string | null);
    const [updateCalculationAttributePayload, setUpdateCalculationAttributePayload] = useState(undefined as {calculationNumber: string, attributeName: string, attributeValue: string} | undefined);
    const [rejectDraftPayload, setRejectDraftPayload] = useState(undefined as {calculationNumber: string, rejectionReason: string} | undefined);

    const { calculation, calculationTemplateMetaData, reviewJournals, generatingReviewJournalsStatus,
        executionPeriod, viewMode, executionResultLines, reversalExecutionResultLines } = calcBuilderState;
    
    
    const [data, setData] = useState([] as JournalLine[]);
    const [reversalData, setReversalData] = useState([] as JournalLine[]);
    const BLANK_EXECUTION_GROUP_OPTION = { label: "Select a group", value: "" };
    const [selectedCLIExecutionGroup, setSelectedCLIExecutionGroup] = useState(BLANK_EXECUTION_GROUP_OPTION);
    
    const [submitForReviewResult, isSubmittingForReview, submitForReviewError] = services.calculationsService.submitCalculationForReview(submitCalculationPayload);
    const [updateCLIAttributeResult, isUpdatingCLIAttributeResult, updateCLIAttributeError] = services.calculationsService.saveCalculationAttribute(updateCalculationAttributePayload);
    const [rejectDraftResult, isRejectingDraft, rejectDraftError] = services.calculationsService.rejectCalculationDraft(rejectDraftPayload);
    const [cliExecutionGroups, cliExecutionGroupsLoading, cliExecutionGroupsError] = services.calculationsService.getCLIExecutionGroups(loadTime);
    const [approvalResult, isApproving, approvalError] = services.calculationsService.approveCalculation(calculationNumberForApproval);

    const [blockReversalFlag, setBlockReversalFlag] = useState(false);
    const [suppressNegChargeFlag, setSuppressNegChargeFlag] = useState(false);
    const [canEditCLIAttributes, setCanEditCLIAttributes] = useState(false);

    const [calculationsByWorkbook, calculationsByWorkbookLoading, calculationsByWorkbookError] = services.calculationsService.getCalculationsByWorkbookId(calculation?.workbookId);


    useEffect(
        () => setLoadTime(new Date().toISOString())
    , []);

    useEffect(() => {
        setCanEditCLIAttributes(!isUpdatingCLIAttributeResult)
    }, [isUpdatingCLIAttributeResult]);

    useEffect(() => {
        setBlockReversalFlag(calculationTemplateMetaData?.blockReversalFlag ? calculationTemplateMetaData.blockReversalFlag : false)
        setSuppressNegChargeFlag(calculationTemplateMetaData?.suppressNegativeChargeFlag ? calculationTemplateMetaData.suppressNegativeChargeFlag : false)
    }, [calculationTemplateMetaData]);

    useEffect( () => {
        setCanEditCLIAttributes(viewMode != CONSTANTS.VIEW_MODE.READ_ONLY && viewMode != CONSTANTS.VIEW_MODE.FROZEN);
    }, [viewMode])
    
    useEffect( () => {
        if (rejectDraftResult == null) {
            return;
        }
        services.messageService.showSuccessAutoDismissBanner("Calculation draft successfully rejected.");
        setShowRejectConfirmation(false);
         // This is triggering a refresh on the calculation data, which is needed to reflect the new status
         calcBuilderDispatch(CALC_BUILDER_ACTIONS.REFRESH_ALL_CALCULATION_INFORMATION);
    }, [rejectDraftResult])

    useEffect(() => {
        if (ArrayUtils.isNullOrEmpty(executionResultLines)) {
            return;
        }
        setData(executionResultLines);
    }, [executionResultLines])

    useEffect(() => {
        setReversalData(reversalExecutionResultLines || []);
    }, [reversalExecutionResultLines])

    useEffect(() => {
        if (executionPeriod != null) {
            return;
        }
        const combinedArray: JournalLine[] = [];
        reviewJournals.forEach(l => combinedArray.push(...l.journalLines));
        setData(combinedArray);
    }, [reviewJournals]) 
    
    useEffect(() => {
        if (submitForReviewResult == null){
            return;
        }
        services.messageService.showSuccessAutoDismissBanner("Calculation successfully submitted for review.");
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.REFRESH_ALL_CALCULATION_INFORMATION);
        setShowSubmitConfirmationModal(false);
    }, [submitForReviewResult])
    
    useEffect(() => {
        if (approvalResult == null){
            return;
        }
        services.messageService.showSuccessAutoDismissBanner("Calculation successfully approved.");
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.REFRESH_ALL_CALCULATION_INFORMATION);
        setShowApproveConfirmation(false);
    }, [approvalResult])
    
    useEffect(() => {
        if (updateCLIAttributeResult == null){
            return;
        }
        // This is triggering a refresh on the calculation data, which is needed to reflect the saved group
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
        setShowUpdateExecutionGroupConfirmation(false);
        if (calculationTemplateMetaData && updateCLIAttributeResult.attributeName == "cliExecutionGroup") {
            calculationTemplateMetaData.cliExecutionGroup = updateCLIAttributeResult.cliExecutionGroup;
        }
        if (updateCLIAttributeResult.autoDraftsCreatedCount > 0){
            services.messageService.showSuccessBanner(`${updateCLIAttributeResult.autoDraftsCreatedCount} auto-draft(s) was(were) created due to the execution group update.`)
        }
    }, [updateCLIAttributeResult])

    useEffect(() => {
        if (StringUtils.isNullOrEmpty(updateCLIAttributeError)) {
            return;
        }
        calcBuilderDispatch(CALC_BUILDER_ACTIONS.CHECK_AND_REFRESH_CALCULATION_INFORMATION);
    }, [updateCLIAttributeError])


    const executeJournalEntryAction = (event: any) => {
        event.stopPropagation();
        switch(event.detail.id){
            case "SUBMIT":
                /*if (!validateReviewerIsAdmin(reviewerUserProfile)) {
                    setValidationModalMessage(`Only admins can review this calculation. To proceed, please ask ${calculation?.accountingOwner} ${cliReviewerAlias}@ (Workbook Reviewer) to request admin access to ATP.`);
                    return;
                }*/
                if (userProfile.user === calculation?.accountingOwnerAlias){
                    setValidationModalMessage("You cannot submit this calculation for review because you are the workook reviewer.");
                    return;
                }
                setShowSubmitConfirmationModal(true);
            break;
            case "APPROVE":
                setShowApproveConfirmation(true);
            break;
            case "REJECT":
                setShowRejectConfirmation(true);
            break;
        }
    }
    
    function getJournalActionItemsByCalculationStatus(): ButtonDropdownProps.ItemOrGroup[] {
        const canReview = userProfile.user === calculation?.accountingOwnerAlias || canReviewAny;
        switch (calculation?.calculationStatus){
            case 'Draft in Progress':
                return [
                    { text: "Submit for Review", id: "SUBMIT", disabled: calculationTemplateMetaData?.cliExecutionGroup == null},
                ]
            case 'Pending Review':
                return canReview?
                [
                    { text: "Approve", id: "APPROVE", disabled: false },
                    { text: "Reject", id: "REJECT", disabled: false },
                ]:
                [
                    { text: "Pending Approval", id: "PENDING", disabled: true, iconName: 'status-pending' },
                ]
        }
        return [];
    }

    function submitForApproval(comment: string): void {
        setSubmitCalculationPayload({calculationNumber: calculation?.calculationNumber || '', notes: comment});
    }

    function approveCalculation(): void {
        setCalculationNumberForApproval(calculation?.calculationNumber);
    }

    function rejectCalculation(rejectReason:string): void {
        setRejectDraftPayload({calculationNumber:calculation?.calculationNumber || '', rejectionReason: rejectReason})        
    }

    function selectCLIExecutionGroup(e:any): void {
        setSelectedCLIExecutionGroup(e.selectedOption);
        setShowUpdateExecutionGroupConfirmation(true);
    }

    function updateCLIExecutionGroup(){
        setUpdateCalculationAttributePayload({calculationNumber: calculation?.calculationNumber || '', attributeName: "cliExecutionGroup", attributeValue: selectedCLIExecutionGroup.value})
    }

    function updateSuppressNegativeChargeFlag() {
        setUpdateCalculationAttributePayload({calculationNumber: calculation?.calculationNumber || '', attributeName: "suppressNegativeCharge", attributeValue: (!suppressNegChargeFlag).toString()})
        setSuppressNegChargeFlag(!suppressNegChargeFlag);
    }

    function updateBlockReversalFlag() {
        setUpdateCalculationAttributePayload({calculationNumber: calculation?.calculationNumber || '', attributeName: "blockReversal", attributeValue: (!blockReversalFlag).toString()})
        setBlockReversalFlag(!blockReversalFlag);
    }

    const showLoadingSpinner = generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.IN_PROGRESS || generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.STARTED;
    
    return (
        <ExpandableSection className="polarisExpandableSection reviewJournalEntriesSection" variant="container"
            defaultExpanded={expanded}
            header={
                <Header><h2>Step 5: Review journal entries</h2></Header>
            }            
        >
            <TPELoadingSpinner loading={showLoadingSpinner} loadingText="Generating journals for review">
                    <div className="calcBuilderContentContainer">
                        <SpaceBetween direction="vertical" size="l">
                            <Container className="polaris-content-container-review-journal-entries"
                                header={
                                    <Header
                                    actions={
                                        executionPeriod == null && CONSTANTS.CALCULATION_STATUS.ACTIVE !== calculation?.calculationStatus && <ButtonDropdown
                                        onItemClick={(event: any) => executeJournalEntryAction(event)}
                                        variant='primary'
                                            items={getJournalActionItemsByCalculationStatus()}
                                            >
                                            Journal Entry Action
                                            </ButtonDropdown>
                                    }
                                    >Original journal entry</Header>
                                }
                            >
                                <div className="journalEntriesContainer">
                                    <JournalEntriesGrid data={data}/>
                                </div>
                            </Container>
                            { executionPeriod != null && !ArrayUtils.isNullOrEmpty(reversalData) &&
                                <Container className="polaris-content-container-review-journal-entries"
                                    header={ <Header>Reversed journal entry</Header> }
                                >
                                    <div className="journalEntriesContainer">
                                        <JournalEntriesGrid data={reversalData}/>
                                    </div>
                                </Container>
                            }
                        </SpaceBetween>
                        <div className="actionButtonsContainer">
                            <Box float="left">
                                <FormField
                                    description="Assign CLI to a Group"
                                    label="Assign CLI to selected Group"
                                    >
                                    <SpaceBetween size="s" direction="horizontal">
                                        <Select className="polarisSmallButtonDropdown"
                                                disabled={!canEditCLIAttributes}
                                                onChange={({ detail }) => selectCLIExecutionGroup(detail)}
                                                selectedOption={calculationTemplateMetaData?.cliExecutionGroup? { label: `Group ${calculationTemplateMetaData?.cliExecutionGroup}`, value: calculationTemplateMetaData?.cliExecutionGroup } : selectedCLIExecutionGroup}
                                                options={cliExecutionGroups.map((x:string) => ({label: `Group ${x}`, value: `${x}`}))} />
                                        { calculationTemplateMetaData?.workbookGroupUpdatedDate &&
                                            <sub>
                                                <Popover
                                                    data-class="basicPopover"
                                                    dismissButton={false}
                                                    position="right"
                                                    triggerType="custom"
                                                    content={`This execution group was updated by user '${calculationTemplateMetaData?.workbookGroupUpdatedBy}' from calculation ${calculationTemplateMetaData?.workbookGroupUpdatedCLI} on ${moment(calculationTemplateMetaData?.workbookGroupUpdatedDate).format("YYYY-MM-DD [at] HH:mm")}`}
                                                    >
                                                        <Link data-class="statusReasonInfoLink" variant="info">Info</Link>
                                                </Popover>
                                            </sub>
                                        }
                                        <Checkbox disabled={!canEditCLIAttributes} checked={blockReversalFlag} onChange={updateBlockReversalFlag}>Flag as non-reversible</Checkbox>
                                        <Checkbox disabled={!canEditCLIAttributes} checked={suppressNegChargeFlag} onChange={updateSuppressNegativeChargeFlag}>Suppress negative charge</Checkbox>
                                        {isUpdatingCLIAttributeResult && <StatusIndicator type={"loading"}/>}
                                    </SpaceBetween>
                                </FormField>
                            </Box>
                        </div>
                        <ATPUserCommentModal
                            key="submitConfirmationModal"
                            visible={showSubmitConfirmationModal}
                            header="Submit calculation for review"
                            content={ <span>
                                        This calculation will be reviewed by <b>{calculation?.accountingOwner}</b> (Workbook Reviewer).
                                        Provide a brief comment and click on <b>Confirm</b> to submit this calculation for review.
                                      </span>
                                    }
                            formFieldLabel="Submit comment"
                            isSaving={isSubmittingForReview}
                            saveError={submitForReviewError}
                            onCancel={() => setShowSubmitConfirmationModal(false)}
                            onSubmit={(comment) => submitForApproval(comment)}
                        />
                        <TPEBasicModal
                            className="roleValidationModal"
                            visible={validationModalMessage != null}
                            action={ACTION_TYPE.OK_ONLY}
                            title="Role validation"
                            onCancel={() => setValidationModalMessage(null)}
                            onConfirm={() => setValidationModalMessage(null)}
                        >
                            <div>
                                {validationModalMessage}
                            </div>
                        </TPEBasicModal>
                        <TPEBasicModal
                            className="approveConfirmModal"
                            visible={showApproveConfirmation}
                            action={ACTION_TYPE.CONFIRM_CANCEL}
                            title="Approve calculation confirmation"
                            onCancel={() => setShowApproveConfirmation(false)}
                            onConfirm={() => approveCalculation()}
                        >
                            <div>
                                Click on <b>Confirm</b> to approve this calculation.
                            </div>
                        </TPEBasicModal>
                        <ATPUserCommentModal
                            key="rejectionConfirmationModal"
                            visible={showRejectConfirmation} 
                            header="Reject calculation"
                            content={<span>Provide a reason and click on <b>Confirm</b> to reject this calculation.</span>}
                            formFieldLabel="Rejection reason"
                            isSaving={isRejectingDraft}
                            saveError={rejectDraftError}
                            onCancel={() => setShowRejectConfirmation(false)}
                            onSubmit={(rejectReason) => rejectCalculation(rejectReason)}
                        />
                        <TPEBasicModal
                            className="updateExecutionGroupConfirmationModal"
                            visible={showUpdateExecutionGroupConfirmation}
                            action={ACTION_TYPE.CONFIRM_CANCEL}
                            title="Update Calculation Execution Group Confirmation"
                            onCancel={() => {
                                setSelectedCLIExecutionGroup(calculationTemplateMetaData?.cliExecutionGroup? { label: `Group ${calculationTemplateMetaData?.cliExecutionGroup}`, value: calculationTemplateMetaData?.cliExecutionGroup } : BLANK_EXECUTION_GROUP_OPTION);
                                setShowUpdateExecutionGroupConfirmation(false);
                            }}
                            onConfirm={() => updateCLIExecutionGroup()}
                        >
                            <div>
                                {ArrayUtils.isNullOrEmpty(calculationsByWorkbook)?
                                    `This will assign group ${selectedCLIExecutionGroup.label} to this calculation. Confirm to proceed.` :
                                    `This will assign group ${selectedCLIExecutionGroup.label} to all calculations in workbook ${calculation?.workbookId}. Confirm to proceed.`
                                }
                            </div>
                        </TPEBasicModal>
                        <TPEErrorWatcher services={services} errors={[submitForReviewError, cliExecutionGroupsError, rejectDraftError]} />
                    </div>
            </TPELoadingSpinner>            
        </ExpandableSection>
    )

}

